import { UnitEditDialogComponent } from '../unit-edit-dialog/unit-edit-dialog.component';
import {
  Component,
  inject,
  model,
  signal,
  computed,
  DestroyRef,
  ChangeDetectionStrategy,
  effect,
} from '@angular/core';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { forkJoin, switchMap, map, catchError, of, finalize, Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { Dialog } from 'primeng/dialog';
import { Drawer } from 'primeng/drawer';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { RadioButton } from 'primeng/radiobutton';
import { Select } from 'primeng/select';
import { FloatLabel } from 'primeng/floatlabel';
import { TableModule } from 'primeng/table';
import { AuthService, NotesService } from '../../services';
import { CodeDescriptionPipe } from '../../pipes/code-description.pipe';
import { UnitAssignmentData, ClaimSearchResult, DispatchTemplate } from '../../models';
import { DispatchActionsConfig } from '../../models/dispatch-actions-config.model';
import { AppraiserService } from '../../services/appraiser.service';
import { AppraiserSearchParams } from '../../models/appraiser.model';
import { ClaimImportService, UnitInfo, RegionInfo } from '../../services/claim-import.service';
import { AssignmentService } from '../../services/assignment.service';
import { CancelActionService } from '../../services/cancel-action.service';
import { DispatchActionsService } from '../../services/dispatch-actions.service';
import { DispatchLogContext } from '../../services/utils/dispatch-xml-builder.util';
import { CreateDispatchUiService } from '../../services/create-dispatch-ui.service';
import {
  GeocodedAddressData,
  AppraiserSelectDialogComponent,
  AppraiserSelectionEvent,
  AppraiserFilter,
  BucketDeclineReason,
  ORDERED_FILTERS,
} from '../appraiser-select-dialog/appraiser-select-dialog.component';
import {
  ActionsSummaryDialogComponent,
  ActionsSummaryData,
} from '../actions-summary-dialog/actions-summary-dialog.component';
import { buildDispatchActionOptions } from '../../services/dispatch-actions-helper.service';
import { isStaffOrIaBucket } from '../../utils';
import { DECLINE_OPTIONS_BY_BUCKET } from '../../constants/decline-options.constants';
import { DispatchActionsConfigService } from '../../services/dispatch-actions-config.service';
import {
  DateFormatter,
  getInspectionAddressByUnitId,
  buildLocationPayload,
  FieldSpec,
  getMissingFieldSpecs,
  buildUnitSavePayload,
  isPolicyRecordPresent,
} from '../../utils';
import { toActionsSummaryData } from '../../models/dispatch-actions.model';

type DispatchType = 'new' | 'manual' | 'partial';

interface ReserveItem {
  unitId: number;
  unitAsgnmntId: number;
  unit: string;
  coverage: string;
  lossType: string;
  rep: string;
  severityQualification?: string;
  unitType?: string;
}

interface DuplicateDispatchRow {
  claimNumber: string;
  claimId: string;
  unit: string;
  coverage: string;
  lossType: string;
  status: string;
  seqType: string;
  assignmentDate: string;
  adjuster: string;
  appraiserName: string;
}

interface EstimateTypeOption {
  label: string;
  value: string;
}

/** Unit data enriched with inspection address fields during claim import */
interface DispatchUnit extends UnitAssignmentData {
  inspLat?: number | null;
  inspLong?: number | null;
  inspAddrLn1?: string;
  inspAddrLn2?: string;
  inspFormattedAddr?: string;
  inspPrecision?: string;
  reserveCovCd?: string;
  reserveLssTypCd?: string;
  /**
   * Set only on fake reserves generated for Partial Manual Dispatch.
   * The `unitId` field is overloaded with a synthetic negative ID to enable
   * individual radio-button selection; this field holds the actual DB unit ID
   * needed for all API calls (getDispatchableUnitDetails, performTransferDispatch, etc.).
   */
  realUnitId?: number;
}

type DispatchClaim = ClaimSearchResult & { unitassignments?: DispatchUnit[] };

@Component({
  selector: 'app-create-dispatch-dialog',
  imports: [
    AsyncPipe,
    TitleCasePipe,
    FormsModule,
    Dialog,
    Drawer,
    Button,
    InputText,
    RadioButton,
    Select,
    FloatLabel,
    TableModule,
    CodeDescriptionPipe,
    AppraiserSelectDialogComponent,
    UnitEditDialogComponent,
    ActionsSummaryDialogComponent,
  ],
  templateUrl: './create-dispatch-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateDispatchDialogComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly claimImportService = inject(ClaimImportService);
  private readonly appraiserService = inject(AppraiserService);
  private readonly dispatchConfigService = inject(DispatchActionsConfigService);
  private readonly dispatchActionsService = inject(DispatchActionsService);
  private readonly notesService = inject(NotesService);
  private readonly authService = inject(AuthService);
  private readonly assignmentService = inject(AssignmentService);
  private readonly cancelActionService = inject(CancelActionService);
  private readonly createDispatchUiService = inject(CreateDispatchUiService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);

  visible = model<boolean>(false);
  drawerVisible = signal(false);
  appraiserDialogVisible = signal(false);
  duplicateModalVisible = signal(false);

  readonly dispatchTypes: { label: string; value: DispatchType }[] = [
    { label: 'New Dispatch', value: 'new' },
    { label: 'Manual Dispatch', value: 'manual' },
    { label: 'Partial Manual Dispatch', value: 'partial' },
  ];

  dispatchType = signal<DispatchType>('new');
  claimNumber = signal('');
  reserves = signal<ReserveItem[]>([]);
  selectedReserveId = signal<number | null>(null);
  loading = signal(false);
  creatingDispatch = signal(false);
  error = signal('');
  duplicateDispatches = signal<DuplicateDispatchRow[]>([]);

  // ── Unit-edit dialog state ──────────────────────────────────────
  unitEditVisible = signal(false);
  /** Full FieldSpec objects for missing/invalid fields — drives unit-edit-dialog. */
  missingFieldSpecs = signal<FieldSpec[]>([]);
  /** Raw unit data snapshot for pre-populating the edit form. */
  currentUnitRawData = signal<Record<string, string>>({});
  /** True = vehicle unit; false = property unit. */
  unitIsVehicle = signal(true);
  /** In-flight state for the unit save API call. */
  savingUnitEdit = signal(false);
  duplicateEstimateType = signal('');
  duplicateModalSubmitted = signal(false);

  // ── Manual Dispatch template state ─────────────────────────────
  /** Available templates loaded once from dispatch/claims?templateInd=Y. */
  templates = signal<DispatchTemplate[]>([]);
  /** Template selected by the user in Manual Dispatch mode. */
  selectedTemplate = signal<DispatchTemplate | null>(null);
  templatesLoading = signal(false);
  templatesLoadError = signal('');

  readonly estimateTypeOptions: EstimateTypeOption[] = [
    { label: 'Supplemental Assist', value: 'SUPPLEMENTAL_ASSIST' },
    { label: 'Secondary Original', value: 'SECONDARY_ORIGINAL' },
  ];

  private claimData = signal<DispatchClaim | null>(null);
  private newClaimId = signal<number | null>(null);

  // Tracks whether the appraiser selection was completed to suppress auto-cancel
  private appraiserConfirmed = false;
  // Prevents duplicate calls to the abandon-cancel API within the same flow
  private cancelCalled = false;
  // True when opened for an already-existing manual dispatch claim (no ESB record to cancel)
  private isExistingClaim = false;

  // Actions summary dialog state
  showActionsSummaryDialog = signal(false);
  actionsSummaryData = signal<ActionsSummaryData | null>(null);
  addressSyncMessage = signal<string | null>(null);
  private pendingDispatchClaimNumber = signal<string | null>(null);

  readonly declineOptions = computed(() =>
    this.duplicateEstimateType() === 'ORIGINAL' ? DECLINE_OPTIONS_BY_BUCKET : null,
  );

  appraiserBaseParams = computed<AppraiserSearchParams | null>(() => {
    const claim = this.claimData();
    const unit = this.getSelectedUnit();
    if (!claim || !unit) return null;
    return {
      companyCd: claim.companyCd ?? '',
      lobCd: claim.lobCd ?? (claim.policy?.['lobCd'] as string) ?? '',
      stateCd: unit.inspState ?? '',
      zipCd: unit.inspZip ?? '',
      countyCd: unit.inspCounty,
      // Mirrors legacy spinner filter: always pass reserveCovCd as altCoverageCd
      // (e.g. 'not_pd', 'not_collsn'). Real reserves fall back to coverageCd when absent.
      coverageCd: unit.reserveCovCd ?? unit.coverageCd ?? '',
      polStCd: claim.policy?.polStCd,
      routingChannelCd:
        claim.routingChannel ?? (claim.policy?.['routingChannelCd'] as string) ?? '',
      unitInfo: this.selectedReserve()?.unit ?? '',
      severityQualification: this.selectedReserve()?.severityQualification,
      unitType: this.selectedReserve()?.unitType,
      assignType: 'DISPATCH',
      refdate: this.appraiserService.calculateReferenceDate(),
      loclat: unit.inspLat != null ? String(unit.inspLat) : undefined,
      loclng: unit.inspLong != null ? String(unit.inspLong) : undefined,
    };
  });

  inspectionAddress = computed(() => {
    const unit = this.getSelectedUnit();
    if (!unit) return '';
    // Returns non-empty if value has at least one non-comma, non-whitespace character
    const isReal = (s: string | undefined): s is string => {
      const t = (s ?? '').trim();
      return t.length > 0 && !/^[,\s]+$/.test(t);
    };
    // Prefer stored formatted address if it isn't a garbage placeholder
    if (isReal(unit.inspFormattedAddr)) return unit.inspFormattedAddr!.trim();
    // Rebuild from individual parts, discarding any comma-only / whitespace-only values
    const parts: string[] = [];
    if (isReal(unit.inspAddrLn1)) parts.push(unit.inspAddrLn1!.trim());
    const cityState = [unit.inspCity, unit.inspState]
      .filter(isReal)
      .map((s) => s!.trim())
      .join(', ');
    if (cityState) parts.push(cityState);
    if (isReal(unit.inspZip)) parts.push(unit.inspZip!.trim());
    return parts.join(', ');
  });

  canContinueDialog = computed(() => {
    if (!this.claimNumber().trim()) return false;
    if (this.dispatchType() === 'manual' && !this.selectedTemplate()) return false;
    return true;
  });
  canContinueDrawer = computed(() => this.selectedReserveId() !== null);
  canContinueWithNewDispatch = computed(() => !!this.duplicateEstimateType());

  readonly drawerTitle = computed(() => {
    const titles: Record<DispatchType, string> = {
      new: 'Create NEW Dispatch',
      manual: 'Create MANUAL Dispatch',
      partial: 'Create PARTIAL MANUAL Dispatch',
    };
    return titles[this.dispatchType()];
  });

  selectedReserve = computed(
    () =>
      this.reserves().find((reserve) => reserve.unitAsgnmntId === this.selectedReserveId()) ?? null,
  );

  /** Prevents (onHide) from clearing seq type/reason when continuing past the duplicate modal. */
  private duplicateContinuing = false;

  constructor() {
    // Cancel the dispatch if the component is destroyed while the appraiser hasn't been selected yet.
    this.destroyRef.onDestroy(() => this.cancelIfAbandoned());

    // Keep step-1 claim number in sync when dialog is opened via URL-driven automation.
    effect(() => {
      if (!this.visible()) {
        return;
      }

      const prefillClaimNumber = this.createDispatchUiService.prefillClaimNumber().trim();
      if (prefillClaimNumber) {
        this.claimNumber.set(prefillClaimNumber);
      }
    });

    // URL-driven action=create: run the same path as pressing step-1 Continue,
    // but skip showing the initial dialog unless an error needs to be displayed.
    effect(() => {
      const autoContinueClaimNumber = this.createDispatchUiService.autoContinueClaimNumber().trim();
      if (!autoContinueClaimNumber) {
        return;
      }

      this.createDispatchUiService.autoContinueClaimNumber.set('');
      this.createDispatchUiService.clearPrefill();
      this.visible.set(false);
      this.dispatchType.set('new');
      this.claimNumber.set(autoContinueClaimNumber);
      this.onDialogContinue();
    });

    // Load templates lazily the first time the user selects Manual Dispatch.
    effect(() => {
      if (this.dispatchType() !== 'manual') return;
      if (this.templates().length > 0 || this.templatesLoading()) return;
      this.loadTemplates();
    });

    // Open reserve selection directly for an already-created manual dispatch claim.
    effect(() => {
      const existing = this.createDispatchUiService.assignExistingClaim();
      if (!existing) return;
      this.createDispatchUiService.assignExistingClaim.set(null);
      this.openReservesForExistingClaim(existing.claimId, existing.claimNum);
    });

    // Mirror reserve-drawer visibility so the sidebar can highlight "Assignment"
    // while the drawer is open (the drawer does not change the route).
    effect(() => {
      this.createDispatchUiService.reserveDrawerOpen.set(this.drawerVisible());
    });
  }

  private loadTemplates(): void {
    this.templatesLoading.set(true);
    this.templatesLoadError.set('');
    this.claimImportService
      .fetchTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (templates) => {
          this.templates.set(templates);
          this.templatesLoading.set(false);
        },
        error: () => {
          this.templatesLoadError.set('Failed to load templates. Please try again.');
          this.templatesLoading.set(false);
        },
      });
  }

  onCancel(): void {
    this.cancelIfAbandoned();
    this.resetAll();
  }

  onDialogContinue(): void {
    if (!this.canContinueDialog()) return;
    this.fetchReserves();
  }

  onDrawerContinue(): void {
    if (!this.canContinueDrawer()) return;
    this.error.set('');
    this.missingFieldSpecs.set([]);
    this.checkForDuplicateDispatches();
  }

  onDuplicateModalCancel(): void {
    this.duplicateModalVisible.set(false);
    if (!this.duplicateContinuing) {
      this.duplicateEstimateType.set('');
    }
    this.duplicateModalSubmitted.set(false);
    this.duplicateContinuing = false;
  }

  onContinueWithNewDispatch(): void {
    this.duplicateModalSubmitted.set(true);
    if (!this.canContinueWithNewDispatch()) return;
    // Flag prevents onHide from clearing values needed at dispatch creation
    this.duplicateContinuing = true;
    this.duplicateModalVisible.set(false);
    this.runDispatchCheckpoint();
  }

  onAppraiserSelected(event: AppraiserSelectionEvent): void {
    if (!event.appraiser) return;
    // Mark confirmed immediately so any concurrent close events don't trigger auto-cancel
    this.appraiserConfirmed = true;
    const appraiser = event.appraiser;
    const claim = this.claimData();
    const unit = this.getSelectedUnit();
    const storedClaimId = this.newClaimId();
    if (!claim || !unit || !storedClaimId) return;

    this.creatingDispatch.set(true);

    const unitName = this.selectedReserve()?.unit;
    const claimNum = claim.claimNum ?? '';
    const estimateType = this.duplicateEstimateType();
    const addressData = event.addressData ?? null;
    const effectiveUnit: DispatchUnit = addressData
      ? this.applyAddressOverride(unit, addressData)
      : unit;
    // realUnitId holds the true DB unit ID for fake (Partial Manual) reserves; unitId otherwise.
    const unitId = unit.realUnitId ?? unit.unitId;

    const baseConfig: DispatchActionsConfig = {
      appointmentId: '',
      claimNumber: claimNum,
      claimSequence: storedClaimId,
      appraiser: appraiser.fullName,
      appraiserGroup: appraiser.userGroupCd ?? '',
      location: effectiveUnit.inspCity
        ? `${effectiveUnit.inspCity}, ${effectiveUnit.inspState ?? ''}`
        : '',
      unitName,
      isNewDispatch: true,
      claimId: storedClaimId,
      transferUserId: appraiser.userId,
      unitAsgnmntId: unit.unitAsgnmntId,
      unitId: unitId != null ? String(unitId) : undefined,
      coverageTypeCd: effectiveUnit.reserveCovCd ?? effectiveUnit.coverageCd,
      reserveLssTypCd: effectiveUnit.reserveLssTypCd,
      timestamps: {},
      presets: {},
    };

    this.dispatchConfigService
      .loadPresetsAndPhone(baseConfig.appointmentId, appraiser.userId)
      .pipe(
        map((result) => ({
          ...baseConfig,
          timestamps: result.timestamps,
          presets: result.presets,
          contactPhone: result.contactPhone,
          stopAllCommInd: result.stopAllCommInd,
        })),
        catchError(() => of(baseConfig)),
        switchMap((config) => {
          const options = buildDispatchActionOptions(config);
          const transferContext: DispatchLogContext = {
            adjusterName: config.appraiser,
            claimNbr: config.claimNumber,
            unitId: config.unitId,
            coverageTypeCd: config.coverageTypeCd,
            reserveLssTypCd: config.reserveLssTypCd,
          };
          return this.dispatchActionsService.performTransferDispatch(
            0,
            options,
            storedClaimId,
            appraiser.userId,
            unit.unitAsgnmntId,
            transferContext,
          );
        }),
        finalize(() => this.creatingDispatch.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          const declineReasons = event.bucketDeclineReasons;
          const claimSeq = String(storedClaimId);
          this.persistEstimateTypeNote(estimateType, claimNum, claimSeq, claim);
          this.persistDeclineReasonsNote(declineReasons, claimNum, claimSeq, claim);

          // Save address only for Staff/IA buckets (not GRP/DTD or Virtual)
          const appraiserGroup = event.row?.group ?? '';
          const shouldSaveAddress = !!addressData && isStaffOrIaBucket(appraiserGroup);
          if (shouldSaveAddress) {
            // Use realUnitId for fake (Partial Manual) reserves so the correct DB record is updated.
            this.syncAddressToClaimsPro(
              storedClaimId,
              unit.realUnitId ?? unit.unitId ?? 0,
              addressData!,
              claim,
            );
          }

          this.addressSyncMessage.set(
            shouldSaveAddress ? 'Inspection address was updated and will sync to ClaimsPro.' : null,
          );

          // Clear pending manual dispatch state now that assignment is booked
          this.createDispatchUiService.pendingManualDispatch.set(null);
          this.createDispatchUiService.clearAllMissingTabs();

          // Show action summary; navigate to basic search with claim number pre-populated on dismiss
          this.pendingDispatchClaimNumber.set(claimNum);
          this.actionsSummaryData.set(toActionsSummaryData(result));
          this.appraiserDialogVisible.set(false);
          this.showActionsSummaryDialog.set(true);
        },
        error: () => {
          this.error.set('Failed to create dispatch. Please try again.');
        },
      });
  }

  onAppraiserDialogCancel(): void {
    this.cancelAppraiserFlowAndExit();
  }

  onAppraiserDialogVisibleChange(visible: boolean): void {
    this.appraiserDialogVisible.set(visible);
    // Handle close via X button or ESC (cancelled event not emitted in those cases)
    if (!visible && !this.appraiserConfirmed) {
      this.cancelAppraiserFlowAndExit();
    }
  }

  /**
   * For manual dispatch (isExistingClaim), stays on the claim overview page so the
   * user can continue editing and re-open reserve selection later.
   */
  private cancelAppraiserFlowAndExit(): void {
    const wasExisting = this.isExistingClaim;
    this.cancelIfAbandoned();
    this.resetAll();
    if (!wasExisting) {
      this.router.navigate(['/assignments']);
    }
  }

  closeActionsSummary(): void {
    this.showActionsSummaryDialog.set(false);
    this.actionsSummaryData.set(null);
    this.addressSyncMessage.set(null);
    const claimNumber = this.pendingDispatchClaimNumber();
    this.pendingDispatchClaimNumber.set(null);
    this.resetAll();
    this.router.navigate(['/assignments'], claimNumber ? { queryParams: { claimNumber } } : {});
  }

  onDrawerCancel(): void {
    this.onCancel();
  }

  onDialogHide(): void {
    if (!this.drawerVisible() && !this.appraiserDialogVisible()) {
      this.cancelIfAbandoned();
      this.resetAll();
    }
  }

  /**
   * Fires the cancel-silent API with reason "abandoned" when the user exits the flow
   * before an appraiser is selected. Safe to call multiple times — guarded by cancelCalled.
   * Falls back to the first available unit if no reserve was explicitly selected yet.
   */
  private cancelIfAbandoned(): void {
    if (this.cancelCalled || this.appraiserConfirmed || this.isExistingClaim) return;
    const claimId = this.newClaimId();
    if (!claimId) return;
    // Collect ALL unit assignments with a valid unitAsgnmntId.
    // In partial manual, multiple unit assignments are created (one per coverage)
    // and all must be cancelled in a single API call.
    const allUnits = this.claimData()?.unitassignments ?? [];
    const unitAsgnmntIds = [
      ...new Set(allUnits.map((u) => u.unitAsgnmntId).filter((id): id is number => !!id)),
    ];
    if (unitAsgnmntIds.length === 0) return;
    this.cancelCalled = true;
    this.cancelActionService
      .executeCancelDispatchBatch(claimId, unitAsgnmntIds, 'abandoned')
      .subscribe({
        error: (err) => console.error('[CreateDispatch] Failed to cancel abandoned dispatch:', err),
      });
  }

  /**
   * Opens the reserve selection drawer for a manual dispatch claim that was
   * already created via claimsBatchAdd. Skips the ESB newDispatch step entirely
   * and fetches reserves directly by claimId — same pipeline as new dispatch from
   * the reserve step onward (unit validation, appraiser selection, performTransferDispatch).
   */
  private openReservesForExistingClaim(claimId: number, claimNum: string): void {
    this.isExistingClaim = true;
    this.cancelCalled = false;
    this.appraiserConfirmed = false;
    this.loading.set(true);
    this.error.set('');
    this.missingFieldSpecs.set([]);
    this.claimNumber.set(claimNum);
    this.newClaimId.set(claimId);
    // Drives the drawer title only — a Partial Manual claim must not be labelled MANUAL.
    this.dispatchType.set(
      this.createDispatchUiService.pendingManualDispatch()?.partialManual ? 'partial' : 'manual',
    );

    this.claimImportService
      .fetchNewDispatchClaim(claimId)
      .pipe(
        switchMap((claim) => {
          // For blank manual dispatches, unitassignments may have unitId=null/0.
          // Patch from the units array which carries the correct unitId.
          this.patchUnitAssignmentIds(claim);
          return this.claimImportService
            .resolveUnitNames(claim)
            .pipe(map((unitNames) => ({ claim, unitNames })));
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ claim, unitNames }) => {
          // Legacy checks Policy before Units ("No claim/policy indicated yet"): a unit can't
          // exist without a policy, so surface the policy gap first rather than a units error.
          const policyExists = isPolicyRecordPresent(claim.policy as Record<string, unknown>);
          if (!policyExists) {
            const detail =
              'No policy info entered yet. Opening Policy so you can add it before assigning an appraiser.';
            this.error.set(detail);
            this.messageService.add({
              severity: 'warn',
              summary: 'Policy required',
              detail,
            });
            this.resetAll();
            this.router.navigate(['/policy', claimNum, String(claimId)]);
            return;
          }
          const units = (claim.unitassignments ?? []) as DispatchUnit[];
          if (units.length === 0) {
            const detail =
              'No reserves/units available for this claim. Opening Units so you can add one before assigning an appraiser.';
            this.error.set(detail);
            this.messageService.add({
              severity: 'warn',
              summary: 'Dispatch context missing',
              detail,
            });
            this.resetAll();
            this.router.navigate(['/units', claimNum, String(claimId)]);
            return;
          }
          const mappedUnits: DispatchUnit[] = units.map((unit) =>
            this.enrichUnitWithAddress(claim, unit),
          );
          this.claimData.set({ ...claim, unitassignments: mappedUnits });
          this.reserves.set(
            mappedUnits.map((unit) => {
              const unitInfo = unitNames.get(unit.unitId ?? 0);
              const unitName =
                unitInfo?.name && !unitInfo.name.startsWith('Unit ')
                  ? unitInfo.name
                  : this.buildUnitNameFromClaim(claim, unit.unitId ?? 0);
              return {
                unitId: unit.unitId ?? 0,
                unitAsgnmntId: unit.unitAsgnmntId ?? unit.unitId ?? 0,
                unit: unitName,
                coverage: unit.reserveCovDescr || unit.coverageDescr || '',
                lossType: unit.reserveLssTypDescr || '',
                rep:
                  unit.unitassignmentadjusters?.find((a) => a.userType === 'REP')?.fullName || '',
                severityQualification: unitInfo?.severityQualification,
                unitType: unitInfo?.unitType,
              };
            }),
          );
          this.drawerVisible.set(true);
        },
        error: () => {
          const detail = 'Failed to load claim reserves. Please try again.';
          this.error.set(detail);
          this.messageService.add({
            severity: 'error',
            summary: 'Get Appraisers Error',
            detail,
          });
        },
      });
  }

  private fetchReserves(): void {
    this.cancelCalled = false;
    this.appraiserConfirmed = false;
    this.loading.set(true);
    this.error.set('');
    this.missingFieldSpecs.set([]);
    const claimNum = this.claimNumber().trim();
    const userId = this.authService.getCurrentUserId();

    // Manual dispatch: clone the selected template via claimsBatchAdd → navigate to claim overview.
    // canContinueDialog() already requires a template for this dispatch type, so this is always set.
    if (this.dispatchType() === 'manual') {
      const template = this.selectedTemplate();
      if (!template) return;
      this.createManualDispatch(claimNum, template, userId);
      return;
    }

    // Partial Manual: the claim IS found in ClaimsPro/Stream, so import it via newDispatch to
    // prepopulate whatever Stream has — but, like Manual Dispatch, skip reserve/appraiser
    // selection here and land on Claim Overview so the user can fill in the rest (units, policy,
    // parties) before assigning an appraiser later via the sidebar's Assignment action.
    if (this.dispatchType() === 'partial') {
      this.createPartialManualDispatch(claimNum, userId);
      return;
    }

    // New Dispatch: import claim from Stream via newDispatch, then show the reserve drawer directly.
    this.claimImportService
      .createNewDispatchRecord(claimNum, 'nj', userId)
      .pipe(
        switchMap((claimId) => {
          this.newClaimId.set(claimId);
          return this.claimImportService.fetchNewDispatchClaim(claimId);
        }),
        switchMap((claim) =>
          this.claimImportService
            .resolveUnitNames(claim)
            .pipe(map((unitNames) => ({ claim, unitNames }))),
        ),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ claim, unitNames }) => {
          const units: UnitAssignmentData[] = claim.unitassignments || [];
          if (units.length === 0) {
            this.error.set('No reserves found for this claim number.');
            if (!this.visible()) this.visible.set(true);
            return;
          }
          const mappedUnits: DispatchUnit[] = units.map((unit) => {
            const dispUnit = unit as DispatchUnit;
            if (dispUnit.realUnitId !== undefined) return dispUnit;
            return this.enrichUnitWithAddress(claim, unit);
          });
          this.claimData.set({ ...claim, unitassignments: mappedUnits });
          this.reserves.set(
            mappedUnits.map((unit) => {
              const lookupId = unit.realUnitId ?? unit.unitId ?? 0;
              const unitInfo: UnitInfo | undefined = unitNames.get(lookupId);
              return {
                unitId: unit.unitId ?? 0,
                unitAsgnmntId: unit.unitAsgnmntId ?? unit.unitId ?? 0,
                unit: unitInfo?.name ?? `Unit ${lookupId}`,
                coverage: unit.reserveCovDescr || unit.coverageDescr || '',
                lossType: unit.reserveLssTypDescr || '',
                rep:
                  unit.unitassignmentadjusters?.find((adjuster) => adjuster.userType === 'REP')
                    ?.fullName || '',
                severityQualification: unitInfo?.severityQualification,
                unitType: unitInfo?.unitType,
              };
            }),
          );
          this.visible.set(false);
          this.drawerVisible.set(true);
        },
        error: (err: Error) => {
          const raw = err.message ?? '';
          this.error.set(
            raw.startsWith('API_ERROR:')
              ? raw.slice('API_ERROR:'.length).trim()
              : raw.toLowerCase().includes('not found')
                ? 'Claim number not found. Please check and try again.'
                : 'Failed to load claim data. Please try again.',
          );
          if (!this.visible()) this.visible.set(true);
        },
      });
  }

  /** Clones the selected template into a new manual dispatch claim via claimsBatchAdd and navigates to claim overview. */
  private createManualDispatch(claimNum: string, template: DispatchTemplate, userId: string): void {
    this.claimImportService
      .fetchTemplateDetails(template.claimId)
      .pipe(
        switchMap((templateData) =>
          this.claimImportService.createManualDispatchFromTemplate(templateData, claimNum, userId),
        ),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (newClaimId) => {
          this.createDispatchUiService.pendingManualDispatch.set({
            claimId: newClaimId,
            claimNum,
            templateUsed: true,
            partialManual: false,
          });
          this.resetAll();
          this.router.navigate(['/claim-overview', claimNum, String(newClaimId)]);
        },
        error: (err: Error) => {
          const message = (err.message ?? '').toLowerCase();
          this.error.set(
            message.includes('not found')
              ? 'Template not found. Please try again.'
              : 'Failed to create manual dispatch. Please try again.',
          );
          if (!this.visible()) this.visible.set(true);
        },
      });
  }

  /**
   * Creates the dispatch record for a claim that exists in ClaimsPro (newDispatch imports the
   * available Stream data) and navigates to claim overview. Reserve and appraiser selection are
   * deferred to the sidebar's Assignment action, which reuses openReservesForExistingClaim.
   * The partialManual flag still matters: the backend persists the fake "No Collision/PD/
   * Comprehensive" reserves at creation time, so they are present when the drawer opens later.
   */
  private createPartialManualDispatch(claimNum: string, userId: string): void {
    this.claimImportService
      .createNewDispatchRecord(claimNum, 'nj', userId, true)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (newClaimId) => {
          this.createDispatchUiService.pendingManualDispatch.set({
            claimId: newClaimId,
            claimNum,
            // Imported claim data already lives in the DB, so claim overview must fetch it
            // rather than render the blank form used for template-less manual dispatches.
            templateUsed: true,
            partialManual: true,
          });
          this.resetAll();
          this.router.navigate(['/claim-overview', claimNum, String(newClaimId)]);
        },
        error: (err: Error) => {
          const raw = err.message ?? '';
          this.error.set(
            raw.startsWith('API_ERROR:')
              ? raw.slice('API_ERROR:'.length).trim()
              : raw.toLowerCase().includes('not found')
                ? 'Claim number not found. Please check and try again.'
                : 'Failed to create partial manual dispatch. Please try again.',
          );
          if (!this.visible()) this.visible.set(true);
        },
      });
  }

  private checkForDuplicateDispatches(): void {
    const unit = this.getSelectedUnit()!;
    const reserveCovCd = unit.reserveCovCd ?? unit.coverageCd ?? '';
    const reserveLssTypCd = unit.reserveLssTypCd ?? '';
    // realUnitId holds the true DB unit ID for fake (Partial Manual) reserves; unitId otherwise.
    const unitId = unit.realUnitId ?? unit.unitId ?? 0;

    const selectedName = this.selectedReserve()?.unit ?? '';

    this.loading.set(true);
    this.claimImportService
      .checkExistingDispatch(this.claimNumber().trim(), reserveCovCd, reserveLssTypCd)
      .pipe(
        // unitId is not unique across a claim's units (e.g. several third-party vehicles can share
        // it), so — like legacy — match on the unit description, resolved per existing dispatch.
        switchMap((claims) =>
          this.resolveExistingUnitNames(claims).pipe(
            map((names) => this.toDuplicateRows(claims, unitId, selectedName, names)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (duplicates) => {
          if (duplicates.length > 0) {
            this.duplicateDispatches.set(duplicates);
            this.duplicateModalVisible.set(true);
            this.loading.set(false);
            return;
          }
          this.duplicateEstimateType.set('ORIGINAL');
          this.runDispatchCheckpoint();
        },
        error: () => {
          this.loading.set(false);
          this.error.set('Unable to verify duplicate dispatches. Please try again.');
        },
      });
  }

  private runDispatchCheckpoint(): void {
    const claim = this.claimData()!;
    const unit = this.getSelectedUnit()!;
    // For fake (Partial Manual) reserves, realUnitId holds the true DB unit ID.
    let unitId = unit.realUnitId ?? unit.unitId!;

    // For blank manual dispatches the unitAssignment may store unitId=0;
    // fall back to the first real unit record's ID from the claim's units array.
    if (!unitId) {
      const rawUnits = this.extractRawUnitArr(claim);
      const firstRealId = rawUnits.length > 0 ? Number(rawUnits[0]['unitId']) : 0;
      if (firstRealId) unitId = firstRealId;
    }

    // Clear the spinner from the preceding duplicate check; this preflight is synchronous.
    this.loading.set(false);

    // Legacy dispatchCheckPoint only validates unit vehicle/property required fields before
    // appraiser selection (no Policy/Loss/Parties gating). The claim was already loaded with
    // unitvehicle/unitproperty expanded, so read from memory instead of re-fetching via
    // getDispatchableUnitDetails (which returns nothing for blank manual dispatches anyway).
    const unitData = this.extractUnitDataFromClaim(claim, unitId);
    const unitVehicle = unitData?.unitVehicle ?? null;
    const unitProperty = unitData?.unitProperty ?? null;
    const unitSubType = unitData?.unitSubType ?? '';

    const missing = getMissingFieldSpecs(unitVehicle, unitProperty, unitSubType);
    if (missing === null) {
      this.error.set('Unable to determine unit type. Please try again or contact support.');
      return;
    }
    if (missing.length > 0) {
      // Store context for unit-edit-dialog
      this.missingFieldSpecs.set(missing);
      this.currentUnitRawData.set((unitVehicle ?? unitProperty ?? {}) as Record<string, string>);
      this.unitIsVehicle.set(unitVehicle != null);
      this.unitEditVisible.set(true);
      return;
    }
    this.drawerVisible.set(false);
    this.appraiserDialogVisible.set(true);
  }

  /**
   * Reads the selected unit's vehicle/property record from the already-loaded claim.
   * Authoritative source for the dispatch preflight (no extra fetch needed).
   */
  private extractUnitDataFromClaim(
    claim: DispatchClaim,
    unitId: number,
  ): {
    unitVehicle: Record<string, string> | null;
    unitProperty: Record<string, string> | null;
    unitSubType: string;
  } | null {
    const rawUnits = this.extractRawUnitArr(claim);
    // Try matching by unitId first, then fall back to the first unit
    const raw = rawUnits.find((u) => Number(u['unitId']) === unitId) ?? rawUnits[0];
    if (!raw) return null;
    const veh = (raw['unitVehicle'] as Record<string, string>) ?? null;
    const prop = (raw['unitProperty'] as Record<string, string>) ?? null;
    if (!veh && !prop) return null;
    return {
      unitVehicle: veh,
      unitProperty: prop,
      unitSubType: (raw['unitSubType'] as string) ?? '',
    };
  }

  /**
   * Called when the user saves the unit-edit-dialog form.
   * Sequence: save unit fields → sync to Stream (awaited) → patch in-memory → re-run checkpoint.
   */
  onUnitEditSaved(fields: Record<string, string>): void {
    const claim = this.claimData()!;
    const unit = this.getSelectedUnit()!;
    const claimId = claim.claimId;
    // For fake (Partial Manual) reserves, realUnitId holds the true DB unit ID.
    const unitId = unit.realUnitId ?? unit.unitId!;
    const isVehicle = this.unitIsVehicle();

    // Legacy always POSTs the exact gridVehicleInfoDialog/gridPropertyInfoDialog field set;
    // build that same whitelisted payload from the previously-fetched record + edits.
    const mergedFields = buildUnitSavePayload(this.currentUnitRawData(), fields, isVehicle);

    this.savingUnitEdit.set(true);
    this.claimImportService
      .saveUnitFieldsAndSync({
        claimId,
        unitId,
        fields: mergedFields,
        updateId: this.authService.getCurrentUserId(),
        isVehicle,
        claimNum: claim.claimNum ?? '',
        polStCd: claim.policy?.polStCd ?? '',
        routingChannelCd:
          claim.routingChannel ?? (claim.policy?.['routingChannelCd'] as string) ?? '',
      })
      .pipe(
        finalize(() => this.savingUnitEdit.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.unitEditVisible.set(false);
          this.missingFieldSpecs.set([]);

          // Patch in-memory so inspectionAddress reflects the saved address immediately
          this.patchUnitAddressInMemory(unitId, fields, isVehicle);

          // All required fields were validated by the edit dialog and saved successfully.
          // Proceed directly to appraiser selection — re-running the checkpoint would
          // hit stale in-memory/API data for blank manual dispatches.
          this.drawerVisible.set(false);
          this.appraiserDialogVisible.set(true);
        },
        error: () => this.error.set('Failed to save unit fields. Please try again.'),
      });
  }

  /** Called when the user dismisses the unit-edit-dialog without saving. */
  onUnitEditCancelled(): void {
    this.unitEditVisible.set(false);
    this.missingFieldSpecs.set([]);
  }

  private static unitNameKey(claimId: unknown, unitId: unknown): string {
    return `${claimId}_${unitId}`;
  }

  /** Resolves the unitInfoJoint description of every unit on the existing-dispatch claims. */
  private resolveExistingUnitNames(claims: ClaimSearchResult[]): Observable<Map<string, string>> {
    const pairs = new Map<string, { claimId: string; unitId: string }>();
    for (const claim of claims) {
      for (const unit of claim.unitassignments ?? []) {
        if (unit.unitId == null) continue;
        pairs.set(CreateDispatchDialogComponent.unitNameKey(claim.claimId, unit.unitId), {
          claimId: String(claim.claimId),
          unitId: String(unit.unitId),
        });
      }
    }
    if (pairs.size === 0) return of(new Map());

    return forkJoin(
      [...pairs.entries()].map(([key, p]) =>
        this.claimImportService
          .resolveUnitInfo(p.claimId, p.unitId)
          .pipe(map((info) => [key, info.name] as const)),
      ),
    ).pipe(map((entries) => new Map(entries)));
  }

  private toDuplicateRows(
    claims: ClaimSearchResult[],
    unitId: number,
    selectedName: string,
    existingNames: Map<string, string>,
  ): DuplicateDispatchRow[] {
    // fetchUnitInfo falls back to "Unit N" when the lookup finds nothing — treat as unresolved.
    const normalize = (name?: string) =>
      name && !name.startsWith('Unit ') ? name.trim().toUpperCase() : '';
    const wanted = normalize(selectedName);
    const rows: DuplicateDispatchRow[] = [];
    for (const claim of claims) {
      const unitAssignments = claim.unitassignments ?? [];
      const dispatches = (claim.assignments ?? []) as any[];
      const appt = ((claim as any).assignappoints ?? [])[0];
      const rawDate = appt?.assignmentDate ?? '';
      const dateObj = rawDate ? DateFormatter.parseLocalDate(rawDate) : null;
      const assignmentDate = dateObj
        ? `${dateObj.getMonth() + 1}/${dateObj.getDate()}/${dateObj.getFullYear()}`
        : '-';

      for (let i = 0; i < unitAssignments.length; i++) {
        const unit = unitAssignments[i];
        const existingName = existingNames.get(
          CreateDispatchDialogComponent.unitNameKey(claim.claimId, unit.unitId),
        );
        const existing = normalize(existingName);
        // Names are the legacy match; fall back to unitId only when either side is unresolved
        // so an unverifiable unit still warns rather than silently allowing a duplicate.
        const isSameUnit =
          existing && wanted ? existing === wanted : (unit.unitId ?? -1) === unitId;
        if (isSameUnit) {
          const dispatch = dispatches[i] ?? dispatches[0] ?? null;
          const appraiser = (unit.unitassignmentappraisers ?? [])[0];
          const rep = (unit.unitassignmentadjusters ?? []).find((a: any) => a.userType === 'REP');
          rows.push({
            claimNumber: claim.claimNum ?? this.claimNumber(),
            claimId: String(claim.claimId ?? '-'),
            unit: existing ? (existingName as string) : selectedName || '-',
            coverage: unit.reserveCovDescr ?? unit.coverageDescr ?? '-',
            lossType: unit.reserveLssTypDescr ?? '-',
            status: dispatch?.claimStatusCd ?? claim.claimStatusCd ?? '-',
            seqType: (dispatch?.['seqTypCd'] as string) ?? '-',
            assignmentDate,
            adjuster: rep?.fullName ?? '-',
            appraiserName: dispatch?.adjusterName ?? appraiser?.fullName ?? '-',
          });
          break;
        }
      }
    }
    return rows;
  }

  private getSelectedUnit(): DispatchUnit | undefined {
    return this.claimData()?.unitassignments?.find(
      (unit) => (unit.unitAsgnmntId ?? unit.unitId) === this.selectedReserveId(),
    );
  }

  /** Returns a copy of the unit with inspection address fields overridden from geocoded data. */
  private applyAddressOverride(unit: DispatchUnit, addressData: GeocodedAddressData): DispatchUnit {
    const formattedAddr = [
      addressData.addressLine1,
      [addressData.city, addressData.state].filter(Boolean).join(', '),
      addressData.zipcode,
    ]
      .filter(Boolean)
      .join(', ');
    return {
      ...unit,
      inspAddrLn1: addressData.addressLine1,
      inspAddrLn2: '',
      inspCity: addressData.city,
      inspState: addressData.state,
      inspZip: addressData.zipcode,
      inspCounty: addressData.county ?? '',
      inspLat: addressData.latitude ?? null,
      inspLong: addressData.longitude ?? null,
      inspFormattedAddr: formattedAddr,
    };
  }

  /**
   * Fire-and-forget: persists the updated address to dispatch/unitVehicles (or unitProperties)
   * then syncs the full unit XML to ClaimsPro via claimImportProfileStatementsAsync.
   * Mirrors the legacy "Save Unit Details → Get Unit Details → Save Profile Statements" chain.
   */
  private syncAddressToClaimsPro(
    claimId: number,
    unitId: number,
    addressData: GeocodedAddressData,
    claim: ClaimSearchResult,
  ): void {
    const isVehicle = this.unitIsVehicle();
    const unitType = isVehicle ? 'unitVehicles' : 'unitProperties';
    const locationData = buildLocationPayload(
      isVehicle,
      claimId,
      unitId,
      addressData,
      this.authService.getCurrentUserId(),
    );
    this.assignmentService
      .updateInspectionLocation(unitType, String(claimId), String(unitId), locationData, {
        claimNumber: claim.claimNum ?? '',
        polStCd: claim.policy?.polStCd ?? '',
        routingChannelCd:
          claim.routingChannel ?? (claim.policy?.['routingChannelCd'] as string) ?? '',
      })
      .subscribe();
  }

  /** Saves the duplicate-dispatch estimate type as its own note. */
  private persistEstimateTypeNote(
    estimateType: string,
    claimNumber: string,
    claimSequence: string,
    claim: ClaimSearchResult,
  ): void {
    if (!estimateType) return;
    const label =
      this.estimateTypeOptions.find((o) => o.value === estimateType)?.label ?? estimateType;
    this.saveDispatchNote(label, claimNumber, claimSequence, claim, 'ESTIMATE TYPE', true);
  }

  /** Saves the per-bucket appraiser decline reasons as their own note. */
  private persistDeclineReasonsNote(
    reasons: Map<AppraiserFilter, BucketDeclineReason>,
    claimNumber: string,
    claimSequence: string,
    claim: ClaimSearchResult,
  ): void {
    const lines: string[] = [];
    for (const bucket of ORDERED_FILTERS) {
      const entry = reasons.get(bucket);
      if (!entry) continue;
      const label =
        entry.reason === 'NO_APPRAISERS_AVAILABLE'
          ? 'No Appraisers Available'
          : entry.reason === 'OTHER'
            ? entry.other
            : (DECLINE_OPTIONS_BY_BUCKET[bucket]?.find((o) => o.value === entry.reason)?.label ??
              entry.reason);
      lines.push(`Declined ${bucket}: ${label}`);
    }
    if (lines.length === 0) return;
    this.saveDispatchNote(lines.join('\n'), claimNumber, claimSequence, claim, 'DECLINED REASON');
  }

  /** Shared infra: persists a note to eClaim + Stream. */
  private saveDispatchNote(
    noteContent: string,
    claimNumber: string,
    claimSequence: string,
    claim: ClaimSearchResult,
    actionName: string,
    skipStream = false,
  ): void {
    const userId = this.authService.getCurrentUserId();
    const polStCd = claim.policy?.polStCd ?? '';
    const routingChannel =
      claim.routingChannel ?? (claim.policy?.['routingChannelCd'] as string) ?? '';

    const streamNote$: Observable<unknown> = skipStream
      ? of(null)
      : this.claimImportService.getRegion(claimNumber, polStCd, routingChannel).pipe(
          // Fall back to 'nj' if region lookup fails so claimImportNotesAsync is always attempted
          catchError(() => of<RegionInfo>({ region: 'nj', streamCompanyId: '201' })),
          switchMap((regionInfo) =>
            this.notesService.saveNoteToStream(
              noteContent,
              claimNumber,
              userId,
              regionInfo.region,
              actionName,
            ),
          ),
          catchError((err) => {
            console.error('Failed to save dispatch note to Stream:', err);
            return of(null);
          }),
        );

    forkJoin([
      this.notesService
        .saveNoteToEClaim({
          claimNumber,
          claimSequence,
          noteType: 'general',
          noteContent,
          isPublic: false,
          userId,
          actionName,
        })
        .pipe(
          catchError((err) => {
            console.error('Failed to save dispatch note to eClaim:', err);
            return of(null);
          }),
        ),
      streamNote$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /**
   * Enriches a raw unit-assignment with inspection-address fields, preferring the
   * saved unit-location address and falling back to the party address by unitId.
   */
  private enrichUnitWithAddress(claim: DispatchClaim, unit: UnitAssignmentData): DispatchUnit {
    const unitLoc = this.extractUnitLocation(claim, unit.unitId);
    const inspAddress = unitLoc ? null : getInspectionAddressByUnitId(claim, unit.unitId);
    return {
      ...unit,
      inspCity: unitLoc?.city || inspAddress?.partyAddrCityName || '',
      inspState: unitLoc?.state || inspAddress?.partyAddrStCd || '',
      inspZip: unitLoc?.zip || inspAddress?.partyAddrZipCd || '',
      inspCounty: unitLoc?.county || inspAddress?.partyAddrSubdName || '',
      inspLat: unitLoc?.lat ?? inspAddress?.partyAddrLat ?? null,
      inspLong: unitLoc?.lng ?? inspAddress?.partyAddrLong ?? null,
      inspAddrLn1: unitLoc?.addrLn1 || inspAddress?.partyAddrLn1Txt || '',
      inspAddrLn2: inspAddress?.partyAddrLn2Txt ?? '',
      inspFormattedAddr: unitLoc?.formattedAddr || inspAddress?.partyAddrFormattedAddr || '',
      inspPrecision: unitLoc?.precision || inspAddress?.partyAddrPrecision || '',
    };
  }

  /**
   * Reads the vehicle/property location address from the unitVehicle/unitProperty
   * nested object returned by the dispatch/claims API (format=json → units is a plain array).
   * This data is updated by syncAddressToClaimsPro and reflects the latest saved address.
   */
  private extractUnitLocation(
    claim: DispatchClaim,
    unitId: number | undefined,
  ): {
    addrLn1: string;
    city: string;
    state: string;
    zip: string;
    county: string;
    lat: number | null;
    lng: number | null;
    formattedAddr: string;
    precision: string;
  } | null {
    if (unitId == null) return null;
    const raw = this.extractRawUnitArr(claim).find((u) => Number(u['unitId']) === unitId);
    if (!raw) return null;

    const toNum = (v: unknown): number | null => (typeof v === 'string' && v ? Number(v) : null);

    const veh = raw['unitVehicle'] as Record<string, string> | undefined;
    if (veh?.['vehLocAddrLn1Txt']) {
      return {
        addrLn1: veh['vehLocAddrLn1Txt'] ?? '',
        city: veh['vehLocCityName'] ?? '',
        state: veh['vehLocStateCd'] ?? '',
        zip: veh['vehLocZipCd'] ?? '',
        county: veh['vehLocSubdName'] ?? '',
        lat: toNum(veh['vehLocLat']),
        lng: toNum(veh['vehLocLong']),
        formattedAddr: veh['vehLocFormattedAddr'] ?? '',
        precision: veh['vehLocPrecision'] ?? '',
      };
    }

    const prop = raw['unitProperty'] as Record<string, string> | undefined;
    if (prop?.['propLocAddrLn1Txt']) {
      return {
        addrLn1: prop['propLocAddrLn1Txt'] ?? '',
        city: prop['propLocCityName'] ?? '',
        state: prop['propLocStateCd'] ?? '',
        zip: prop['propLocZipCd'] ?? '',
        county: prop['propLocSubdName'] ?? '',
        lat: toNum(prop['propLocLat']),
        lng: toNum(prop['propLocLong']),
        formattedAddr: prop['propLocFormattedAddr'] ?? '',
        precision: prop['propLocPrecision'] ?? '',
      };
    }

    return null;
  }

  /**
   * Patches the saved address onto the matching unit(s) in claimData so inspectionAddress
   * updates instantly, no re-fetch needed. `unitId` is the real DB id, so it matches both
   * real rows (`unitId`) and every fake Partial Manual row (`realUnitId`) for that unit.
   * inspFormattedAddr is cleared so inspectionAddress rebuilds it from the new parts.
   */
  private patchUnitAddressInMemory(
    unitId: number,
    fields: Record<string, string>,
    isVehicle: boolean,
  ): void {
    const prefix = isVehicle ? 'vehLoc' : 'propLoc';
    this.claimData.update((claim) => {
      if (!claim?.unitassignments) return claim;
      return {
        ...claim,
        unitassignments: claim.unitassignments.map((u): DispatchUnit => {
          const du = u as DispatchUnit;
          // Match real reserves by unitId; match fake reserves by realUnitId.
          if (du.unitId !== unitId && du.realUnitId !== unitId) return du;
          const lat = fields[`${prefix}Lat`];
          const long = fields[`${prefix}Long`];
          return {
            ...du,
            inspAddrLn1: fields[`${prefix}AddrLn1Txt`] ?? du.inspAddrLn1,
            inspCity: fields[`${prefix}CityName`] ?? du.inspCity,
            inspState: fields[`${prefix}StateCd`] ?? du.inspState,
            inspZip: fields[`${prefix}ZipCd`] ?? du.inspZip,
            inspCounty: fields[`${prefix}SubdName`] ?? du.inspCounty,
            // Thread the geocoded coordinates through so the appraiser search sends
            // loclat/loclng (distance sort) — matches the legacy dispatch/spinner payload.
            inspLat: lat ? Number(lat) : du.inspLat,
            inspLong: long ? Number(long) : du.inspLong,
            inspFormattedAddr: '', // cleared so inspectionAddress() rebuilds from parts above
          };
        }),
      };
    });
  }

  /** Returns the raw units array from `claim['units']` (plain array in format=json response). */
  private extractRawUnitArr(claim: DispatchClaim): Record<string, unknown>[] {
    const units = claim['units'];
    if (!Array.isArray(units)) return [];
    return units as Record<string, unknown>[];
  }

  /**
   * For blank manual dispatches, unitassignments may have unitId=null/0 while
   * the units array has the correct unitId. Patch in-place so downstream lookups
   * (resolveUnitNames, getDispatchableUnitDetails) use the real ID.
   */
  private patchUnitAssignmentIds(claim: DispatchClaim): void {
    const rawUnits = this.extractRawUnitArr(claim);
    if (rawUnits.length === 0) return;
    const assignments = claim.unitassignments ?? [];
    for (const ua of assignments) {
      if (ua.unitId) continue;
      // Match by index (single-unit blank claims) or use the first real unitId
      const realId = Number(rawUnits[0]['unitId']) || 0;
      if (realId) ua.unitId = realId;
    }
  }

  /**
   * Builds a human-readable unit name from raw vehicle/property data when
   * the unitInfoJoint code is unavailable (blank manual dispatch).
   */
  private buildUnitNameFromClaim(claim: DispatchClaim, unitId: number): string {
    const raw = this.extractRawUnitArr(claim).find((u) => Number(u['unitId']) === unitId);
    if (!raw) return `Unit ${unitId}`;
    const veh = raw['unitVehicle'] as Record<string, string> | undefined;
    if (veh) {
      const parts = [
        veh['vehYearTxt'],
        veh['vehMakeTxt'],
        veh['vehModelTxt'],
        veh['vinTxt'],
      ].filter(Boolean);
      return parts.length > 0 ? `veh-${parts.join(' ')}` : `Unit ${unitId}`;
    }
    const prop = raw['unitProperty'] as Record<string, string> | undefined;
    if (prop?.['propLocAddrLn1Txt']) {
      return `prop-${prop['propLocAddrLn1Txt']}`;
    }
    return `Unit ${unitId}`;
  }

  resetAll(): void {
    this.visible.set(false);
    this.drawerVisible.set(false);
    this.appraiserDialogVisible.set(false);
    this.duplicateModalVisible.set(false);
    this.dispatchType.set('new');
    this.claimNumber.set('');
    this.reserves.set([]);
    this.selectedReserveId.set(null);
    this.loading.set(false);
    this.creatingDispatch.set(false);
    this.error.set('');
    this.duplicateDispatches.set([]);
    this.duplicateEstimateType.set('');
    this.duplicateModalSubmitted.set(false);
    this.claimData.set(null);
    this.newClaimId.set(null);
    this.duplicateContinuing = false;
    this.isExistingClaim = false;
    // Template selection is cleared but the loaded list is kept (avoid re-fetching on next open).
    this.selectedTemplate.set(null);
    this.templatesLoadError.set('');
    this.createDispatchUiService.clearPrefill();
  }
}
